// src/context/AdminAuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { adminAuthAPI } from '../services/adminAuth';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load admin on app start
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedAdmin = localStorage.getItem('adminUser');
    
    if (token && storedAdmin) {
      try {
        const adminData = JSON.parse(storedAdmin);
        if (adminData.role === 'admin' || adminData.role === 'super_admin') {
          setAdmin(adminData);
        } else {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
        }
      } catch {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    }
    setLoading(false);
  }, []);

  // Admin login
  const login = async (email, password) => {
    try {
      setError(null);
      const result = await adminAuthAPI.login(email, password);
      
      if (result.success) {
        const { user, token } = result;
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));
        setAdmin(user);
        return { success: true, user };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Admin logout
  const logout = async () => {
    await adminAuthAPI.logout();
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setAdmin(null);
  };

  // Update admin data
  const updateAdmin = (updatedAdmin) => {
    setAdmin(updatedAdmin);
    localStorage.setItem('adminUser', JSON.stringify(updatedAdmin));
  };

  const value = {
    admin,
    loading,
    error,
    login,
    logout,
    updateAdmin,
    isAuthenticated: !!admin,
    isAdmin: admin?.role === 'admin' || admin?.role === 'super_admin',
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};